/**
 * GeometriesListCore Component
 *
 * Core reusable component for displaying and managing geometries.
 * Supports both edit and view modes with different action handlers.
 *
 * @module components/map/GeometriesList/core
 */

import { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconPolygon } from '@tabler/icons-react';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { PillTabs } from '../../../ui/PillTabs';
import type { PillTab } from '../../../ui/PillTabs';
import { GeometryType } from '../../../../types/geometry';
import { GeometryListItem } from './GeometryListItem';
import { BulkActionsBar } from './BulkActionsBar';
import { EmptyState } from '@/components/common/EmptyState';
import type { DefaultGeometryFilter, GeometriesListCoreProps } from '../types';
import classes from '../styles/GeometriesListCore.module.css';

/**
 * GeometriesListCore Component
 *
 * Mode-agnostic list component that handles both edit and view modes.
 *
 * Generic over `TFilter` so consumers can pass a custom set of pill tabs
 * (e.g. Obstructions uses `All | AffectedSection | DetourRoute |
 * SignPlacement`). When `tabs` and `geometryFilter` are omitted, the
 * default Marker / Line / Shape taxonomy is used.
 */
const GeometriesListCoreInner = <
  TFilter extends string = DefaultGeometryFilter,
>({
  geometries,
  mode,
  selectedId,
  onSelect,
  selectedIds = [],
  onSelectionChange,
  onClearSelection,
  onDelete,
  onBulkDelete,
  onRename,
  onZoomTo,
  onHighlight,
  showCheckboxes = false,
  showActions = false,
  emptyMessage,
  className,
  tabs: customTabs,
  geometryFilter: customGeometryFilter,
}: GeometriesListCoreProps<TFilter>) => {
  const { t } = useTranslation();
  const hasCustomTabs = customTabs !== undefined;

  type FilterValue = TFilter | DefaultGeometryFilter;
  const DEFAULT_ALL: DefaultGeometryFilter = 'all';

  const [activeFilter, setActiveFilter] = useState<FilterValue>(() =>
    hasCustomTabs ? (customTabs[0]?.value ?? DEFAULT_ALL) : DEFAULT_ALL
  );

  // Track whether the default-taxonomy "All" tab was visible last render —
  // when it appears (1 type → many), auto-switch focus to "All".
  const hadDefaultAllTabRef = useRef(false);

  const geometryCounts = useMemo(() => {
    const counts = { all: geometries.length, Marker: 0, Line: 0, Shape: 0 };
    for (const geometry of geometries) {
      if (geometry.type === GeometryType.Marker) counts.Marker++;
      else if (geometry.type === GeometryType.Line) counts.Line++;
      else if (geometry.type === GeometryType.Shape) counts.Shape++;
    }
    return counts;
  }, [geometries]);

  // Caller-provided `customTabs` win when supplied. Otherwise: single type
  // → only that type tab (no "All"); multiple types → prepend "All" then
  // each present type.
  const geometryTabs = useMemo<PillTab<FilterValue>[]>(() => {
    if (hasCustomTabs) {
      return customTabs as PillTab<FilterValue>[];
    }

    const typeTabs: PillTab<DefaultGeometryFilter>[] = [];

    if (geometryCounts.Marker > 0) {
      typeTabs.push({
        value: 'Marker',
        labelKey: 'geometries.tabs.markers',
        count: geometryCounts.Marker,
      });
    }
    if (geometryCounts.Line > 0) {
      typeTabs.push({
        value: 'Line',
        labelKey: 'geometries.tabs.lines',
        count: geometryCounts.Line,
      });
    }
    if (geometryCounts.Shape > 0) {
      typeTabs.push({
        value: 'Shape',
        labelKey: 'geometries.tabs.shapes',
        count: geometryCounts.Shape,
      });
    }

    if (typeTabs.length <= 1) return typeTabs;

    return [
      {
        value: 'all',
        labelKey: 'geometries.tabs.all',
        count: geometryCounts.all,
      },
      ...typeTabs,
    ];
  }, [hasCustomTabs, customTabs, geometryCounts]);

  useEffect(() => {
    if (geometryTabs.length === 0) return;
    const availableValues = geometryTabs.map((tab) => tab.value);

    if (!hasCustomTabs) {
      const hasDefaultAll = availableValues.includes(DEFAULT_ALL);
      if (hasDefaultAll && !hadDefaultAllTabRef.current) {
        setActiveFilter(DEFAULT_ALL);
      } else if (!availableValues.includes(activeFilter)) {
        setActiveFilter(
          hasDefaultAll ? DEFAULT_ALL : (geometryTabs[0]?.value ?? DEFAULT_ALL)
        );
      }
      hadDefaultAllTabRef.current = hasDefaultAll;
      return;
    }

    if (!availableValues.includes(activeFilter)) {
      const fallback = geometryTabs[0]?.value;
      if (fallback !== undefined) {
        setActiveFilter(fallback);
      }
    }
  }, [geometryTabs, activeFilter, hasCustomTabs]);

  // Filter geometries by active tab (no sorting - preserve insertion order)
  const filteredGeometries = useMemo(() => {
    if (hasCustomTabs && customGeometryFilter) {
      return geometries.filter((geometry) =>
        customGeometryFilter(geometry, activeFilter as TFilter)
      );
    }
    if (activeFilter === 'all') return geometries;
    return geometries.filter((geometry) => geometry.type === activeFilter);
  }, [hasCustomTabs, customGeometryFilter, geometries, activeFilter]);

  const handleBulkDelete = useCallback(() => {
    if (onBulkDelete && selectedIds.length > 0) {
      onBulkDelete(selectedIds);
    }
  }, [onBulkDelete, selectedIds]);

  const handleItemClick = useCallback(
    (id: string) => {
      if (onSelect) {
        onSelect(id);
      }
      if (mode === 'view' && onHighlight) {
        onHighlight(id);
      }
    },
    [onSelect, onHighlight, mode]
  );

  if (geometries.length === 0) {
    const message = emptyMessage || t('geometries.emptyState');
    const hint = mode === 'edit' ? t('geometries.emptyStateHint') : undefined;

    return (
      <div className={`${classes.container} ${className || ''}`}>
        <EmptyState
          icon={<IconPolygon size={48} />}
          title={message}
          description={hint}
        />
      </div>
    );
  }

  const selectedCount = selectedIds.length;
  const hasSelection = selectedCount > 0;

  return (
    <div className={`${classes.container} ${className || ''}`}>
      {mode === 'edit' && hasSelection && onClearSelection && (
        <BulkActionsBar
          selectedCount={selectedCount}
          onDeleteSelected={handleBulkDelete}
          onClearSelection={onClearSelection}
        />
      )}

      <div className={classes.pillTabsWrapper}>
        <PillTabs
          tabs={geometryTabs}
          activeTab={activeFilter}
          onTabChange={setActiveFilter}
          variant="compact"
        />
      </div>

      <AppScrollArea fill>
        <div className={classes.scrollAreaInner}>
          {filteredGeometries.map((geometry) => (
            <GeometryListItem
              key={geometry.id}
              geometry={geometry}
              mode={mode}
              isSelected={geometry.id === selectedId}
              isChecked={selectedIds.includes(geometry.id)}
              showCheckbox={showCheckboxes}
              showActions={showActions}
              onClick={() => handleItemClick(geometry.id)}
              onCheckboxChange={() => onSelectionChange?.(geometry.id)}
              onDelete={() => onDelete?.(geometry.id)}
              onRename={(newName) => onRename?.(geometry.id, newName)}
              onZoomTo={() => onZoomTo?.(geometry)}
            />
          ))}
        </div>
      </AppScrollArea>
    </div>
  );
};

// React.memo strips the generic parameter — re-cast to preserve `<TFilter>`
// at call sites while keeping shallow-prop memoization.
export const GeometriesListCore = memo(
  GeometriesListCoreInner
) as typeof GeometriesListCoreInner;
